import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import useAuthStore from '../../store/authStore';
import useViewOnly from '../../hooks/useViewOnly';
import Dashboard from '../../pages/Dashboard';
import CategoryList from '../../pages/categories/CategoryList';
import AccountList from '../../pages/accounts/AccountList';
import ProductList from '../../pages/products/ProductList';
import DocumentList from '../../pages/documents/DocumentList';
import DocumentForm from '../../pages/documents/DocumentForm';
import StockMovements from '../../pages/stock/StockMovements';
import CurrentStock from '../../pages/stock/CurrentStock';
import AccountLedger from '../../pages/accounts/AccountLedger';
import AccountStatement from '../../pages/accounts/AccountStatement';
import CashFlow from '../../pages/cashflow/CashFlow';
import ExpenseList from '../../pages/expenses/ExpenseList';
import Reports from '../../pages/reports/Reports';
import POSStation from '../../pages/pos/POSStation';
import Settings from '../../pages/Settings';
import Stock from '../../pages/stock/Stock';
import Products from '../../pages/products/Products';
import DeliveryBoard from '../../pages/delivery/DeliveryBoard';
import Accounting from '../../pages/accounting/Accounting';
import Finance from '../../pages/finance/Finance';
import Hr from '../../pages/hr/Hr';
import MyWork from '../../pages/hr/MyWork';
import DriverSummary from '../../pages/delivery/DriverSummary';
import Modifiers from '../../pages/modifiers/Modifiers';
import AIChatWidget from '../AIChatWidget';
import HealthCheck from '../../pages/ai/HealthCheck';
import Assistant from '../../pages/ai/Assistant';
import ShiftWidget from '../ShiftWidget';
import useOfflineSync from '../../hooks/useOfflineSync';
import { usePushSubscription } from '../../hooks/usePushSubscription';

// 56px = height of mobile top bar
const MOBILE_BAR = 56;

// 100dvh tracks the REAL visible viewport on mobile (URL bar shown/hidden);
// fall back to 100vh on older browsers. Fixes footer buttons hidden behind browser chrome.
const VH = (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('height', '100dvh')) ? '100dvh' : '100vh';

/* ─── role-based route guard ─────────────────────────────────────────────── */
// where each role lands by default
const homeFor = (role) => (role === 'driver' ? '/delivery' : '/');

// MMM Fashion shop: selling happens on its computer — the web only shows (sales, reports, stock…)
const NoSellingHere = ({ children }) => {
  const viewOnly = useViewOnly();
  if (!viewOnly) return children;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 24 }}>
      <div style={{ maxWidth: 460, textAlign: 'center', background: '#fff', borderRadius: 16, padding: '32px 28px', boxShadow: '0 4px 24px rgba(0,0,0,.06)' }}>
        <div style={{ fontSize: 44 }}>👟</div>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: '10px 0 6px' }} dir="rtl">البيع يتم من الكمبيوتر في المحل</h2>
        <p style={{ color: '#6b7280', margin: '0 0 6px' }} dir="rtl">هذا الحساب يعمل على برنامج MMM Fashion. هنا يمكنك رؤية المبيعات والفواتير والتقارير والمخزون.</p>
        <p style={{ color: '#6b7280', margin: '0 0 18px', fontSize: 13 }}>Selling is done on the shop computer (MMM Fashion). Here you can see sales, invoices, reports and stock.</p>
        <a href="/" style={{ display: 'inline-block', background: '#2e9f86', color: '#fff', padding: '10px 22px', borderRadius: 10, fontWeight: 700, textDecoration: 'none' }}>Dashboard</a>
      </div>
    </div>
  );
};

const RequireRole = ({ roles, children }) => {
  const user = useAuthStore((s) => s.user);
  const role = user?.role || 'viewer';
  if (!roles.includes(role)) return <Navigate to={homeFor(role)} replace />;
  return children;
};

// "/" — drivers go to delivery board, everyone else sees the dashboard
const Home = () => {
  const user = useAuthStore((s) => s.user);
  if (user?.role === 'driver') return <Navigate to="/delivery" replace />;
  return <Scroll><Dashboard /></Scroll>;
};

// role groups
const STAFF = ['admin', 'manager', 'cashier', 'viewer'];      // everything except driver
const SELLERS = ['admin', 'manager', 'cashier'];              // can use POS / create docs
const MGMT = ['admin', 'manager'];                            // stock, cashflow, reports, expenses
const DELIVERY = ['admin', 'manager', 'cashier', 'driver'];   // delivery board

// Full-height pages: no padding, manage own scroll.
const Full = ({ children }) => (
  <div style={{ height: VH, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
    {/* spacer only on mobile */}
    <div className="block md:hidden" style={{ height: MOBILE_BAR, flexShrink: 0 }} />
    <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
      {children}
    </div>
  </div>
);

// Normal pages: padded + scrollable.
const Scroll = ({ children }) => (
  <div style={{ height: VH, overflow: 'auto', boxSizing: 'border-box', padding: 24 }}>
    {/* spacer only on mobile */}
    <div className="block md:hidden" style={{ height: MOBILE_BAR }} />
    {children}
  </div>
);

export default function Layout() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role || 'viewer';
  const { isOnline, queueCount, syncing } = useOfflineSync();
  // Auto-subscribe to push notifications if permission already granted
  usePushSubscription(false);

  return (
    <div style={{ display: 'flex', height: VH, overflow: 'hidden' }}>
      <Sidebar />
      <main style={{ flex: 1, minWidth: 0, height: VH, overflow: 'hidden', position: 'relative' }}>
        {/* ── offline / sync banner ── */}
        {!isOnline && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 9999, background: '#dc2626', color: '#fff', textAlign: 'center', fontSize: 12, fontWeight: 700, padding: '6px 12px', lineHeight: 1.4 }}>
            ⚠️ أنت غير متصل بالإنترنت — المبيعات تُحفظ تلقائياً · Offline — sales are saved locally
            {queueCount > 0 && ` (${queueCount} pending)`}
          </div>
        )}
        {isOnline && syncing && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 9999, background: '#f59e0b', color: '#78350f', textAlign: 'center', fontSize: 12, fontWeight: 700, padding: '6px 12px' }}>
            ⏳ جاري المزامنة... Syncing {queueCount} pending {queueCount === 1 ? 'sale' : 'sales'}…
          </div>
        )}
        <AIChatWidget />
        <ShiftWidget />
        <Routes>
          {/* full-height, self-scrolling pages */}
          <Route path="/pos"           element={<RequireRole roles={SELLERS}><NoSellingHere><Full><POSStation /></Full></NoSellingHere></RequireRole>} />
          <Route path="/products"      element={<RequireRole roles={STAFF}><Full><Products /></Full></RequireRole>} />
          <Route path="documents/new"  element={<RequireRole roles={SELLERS}><NoSellingHere><Full><DocumentForm /></Full></NoSellingHere></RequireRole>} />
          <Route path="documents/:id"  element={<RequireRole roles={STAFF}><Full><DocumentForm /></Full></RequireRole>} />

          {/* normal padded/scrollable pages */}
          <Route path="/"              element={<Home />} />
          <Route path="/accounts"      element={<RequireRole roles={STAFF}><Scroll><AccountList /></Scroll></RequireRole>} />
          <Route path="/documents"     element={<RequireRole roles={STAFF}><Scroll><DocumentList /></Scroll></RequireRole>} />
          <Route path="/accounts/:id/ledger"    element={<RequireRole roles={STAFF}><Scroll><AccountLedger /></Scroll></RequireRole>} />
          <Route path="/accounts/:id/statement" element={<RequireRole roles={STAFF}><Scroll><AccountStatement /></Scroll></RequireRole>} />
          <Route path="/cashflow"      element={<RequireRole roles={MGMT}><Scroll><CashFlow /></Scroll></RequireRole>} />
          <Route path="/expenses"      element={<RequireRole roles={MGMT}><Scroll><ExpenseList /></Scroll></RequireRole>} />
          <Route path="/reports"       element={<RequireRole roles={MGMT}><Scroll><Reports /></Scroll></RequireRole>} />
          <Route path="/settings"      element={<RequireRole roles={['admin']}><Scroll><Settings /></Scroll></RequireRole>} />
          {/* link-only — intentionally NOT in the sidebar */}
          <Route path="/accounting"    element={<RequireRole roles={['admin']}><Scroll><Accounting /></Scroll></RequireRole>} />
          <Route path="/finance"       element={<RequireRole roles={MGMT}><Scroll><Finance /></Scroll></RequireRole>} />
          <Route path="/hr"            element={<RequireRole roles={['admin', 'manager', 'accountant']}><Scroll><Hr /></Scroll></RequireRole>} />
          <Route path="/my-work"       element={<RequireRole roles={['admin', 'manager', 'accountant', 'cashier', 'viewer', 'waiter', 'kitchen', 'driver']}><Scroll><MyWork /></Scroll></RequireRole>} />
          <Route path="/stock"         element={<RequireRole roles={MGMT}><Scroll><Stock /></Scroll></RequireRole>} />
          <Route path="/delivery"      element={<RequireRole roles={DELIVERY}><Scroll><DeliveryBoard /></Scroll></RequireRole>} />
          <Route path="/driver-summary" element={<RequireRole roles={['admin', 'manager', 'driver']}><Scroll><DriverSummary /></Scroll></RequireRole>} />
          <Route path="/modifiers"     element={<RequireRole roles={MGMT}><Scroll><Modifiers /></Scroll></RequireRole>} />
          {/* paid AI add-ons (the server refuses them when off) */}
          <Route path="/health-check"  element={<RequireRole roles={['admin']}><Scroll><HealthCheck /></Scroll></RequireRole>} />
          <Route path="/assistant"     element={<RequireRole roles={STAFF}><Full><Assistant /></Full></RequireRole>} />

          {/* anything else -> role home */}
          <Route path="*" element={<Navigate to={homeFor(role)} replace />} />
        </Routes>
      </main>
    </div>
  );
}